import type React from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

interface PopoutState {
	isPoppedOut: boolean; // delete after cleaning up local storage flag
	lastVisitedSearch: string | null;
	content: React.ReactNode;
}

const LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH = 'jira-ops-alert-filters-last-visited-search';
const localStorageProvider = createLocalStorageProvider('alert_filters_popout');

const setAlertFiltersLastVisitedInLocalStorage = (value: PopoutState['lastVisitedSearch']) => {
	try {
		localStorageProvider.set(LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH, value);
	} catch (_) {
		return false;
	}
	return true;
};
const getAlertFiltersLastVisitedFromLocalStorage = (): string | undefined => {
	let item;
	try {
		item = localStorageProvider.get(LOCAL_STORAGE_KEY_LAST_VISITED_SEARCH);
	} catch (_) {
		return undefined;
	}
	return item;
};

const getOnInit = () => {
	return fg('operations_nav4_remaining_items')
		? {
				lastVisitedSearch: getAlertFiltersLastVisitedFromLocalStorage(),
			}
		: {
				isPoppedOut: false,
				content: null,
			};
};

const actions = {
	setIsPoppedOut:
		(value: boolean): Action<PopoutState> =>
		({ getState, setState }) => {
			if (!fg('operations_nav4_remaining_items')) {
				setState({ ...getState(), isPoppedOut: value });
			}
		},
	setLastVisitedSearch:
		(value: string | null): Action<PopoutState> =>
		({ getState, setState }) => {
			if (getState().lastVisitedSearch) {
				setState({ ...getState(), lastVisitedSearch: value });
				setAlertFiltersLastVisitedInLocalStorage(value);
			}
		},
	togglePoppedOut:
		(value: string): Action<PopoutState> =>
		({ getState, setState }) => {
			if (!getState().lastVisitedSearch) {
				setState({ ...getState(), lastVisitedSearch: value });
				setAlertFiltersLastVisitedInLocalStorage(value);
			} else {
				setState({ ...getState(), lastVisitedSearch: null });
				setAlertFiltersLastVisitedInLocalStorage(null);
			}
		},
	syncLastVisitedSearch:
		(): Action<PopoutState> =>
		({ getState, setState }) => {
			setState({ ...getState(), lastVisitedSearch: getAlertFiltersLastVisitedFromLocalStorage() });
		},
	setContent:
		(content: React.ReactNode): Action<PopoutState> =>
		({ getState, setState }) => {
			setState({ ...getState(), content });
		},
};

const Store = createStore<PopoutState, typeof actions>({
	initialState: {
		content: null,
		isPoppedOut: false, // delete after cleaning up local storage flag
		lastVisitedSearch: ' ',
	},
	actions,
	name: 'AlertsPopoutStore',
});
export const AlertsPopoutStoreContainer = createContainer(Store, {
	onInit:
		() =>
		({ setState }) => {
			setState(getOnInit());
		},
});

const useHook = createHook(Store);
export const useAlertsPopoutStore = () => {
	const [state, hookActions] = useHook();
	return {
		state,
		...hookActions,
	};
};
